// react-grid-layout v2 bundles react-draggable, whose log() reads
// `process.env.DRAGGABLE_DEBUG` on every drag/resize start. `process` is
// undefined in the browser, so without this the gesture throws a
// ReferenceError and aborts (drag/resize silently dead). log() is lazy — only
// called on interaction — so a runtime shim here (well before any drag) is
// enough, and covers both dev prebundle and the production bundle.
;(globalThis as unknown as { process?: { env: Record<string, unknown> } }).process ??= { env: {} }

import { Amplify } from 'aws-amplify'
import { createRoot } from 'react-dom/client'
import { Provider as ReduxProvider } from 'react-redux'
import { store } from '@/store'
import { COGNITO } from '@/config/constants'
import { SettingsProvider } from '@/context/settings-context'
import { ChartInteractionProvider } from '@/context/chart-interaction-context'
import { DeviceProvider } from '@/context/device-context'
import { AuthProvider } from '@/features/auth/components/auth-provider'
import { loadRewardful } from '@/services/affiliate/rewardful'
import { AnalyticsProvider } from '@/services/analytics'
import { ChartColorPickerProvider } from '@/features/charts/chart-color-picker-provider'
import { ChartThemeColorsProvider } from '@/features/charts/chart-theme-colors-provider'
import { syncTitleBarAttribute } from '@/services/platform/title-bar'
import App from './App'
import './index.css'

// Debug: catch crashes that cause page refreshes
// "ResizeObserver loop completed with undelivered notifications" (and the older
// "...loop limit exceeded") is a benign, non-actionable browser quirk: it fires
// when a resize callback schedules more layout than fits in a single frame —
// extremely common with React Flow's NodeResizer while dragging a group/subnet
// larger. It is NOT a crash and must not pollute the crash log.
const isBenignResizeObserverError = (message?: string): boolean =>
  !!message && message.includes('ResizeObserver loop')

const graphStatus = window.__ettGraphStatus ??
  window.__ettDevGraphStatus ?? {
    state: 'booting' as const,
    buildMode: import.meta.env.MODE,
    reason: 'waiting for editor mount',
    updatedAt: null,
  }
window.__ettGraphStatus = graphStatus
window.__ettDevGraphStatus = graphStatus

if (import.meta.env.DEV) {
  window.addEventListener('error', (e) => {
    if (isBenignResizeObserverError(e.message)) return
    const entry = {
      type: 'error',
      message: e.message,
      filename: e.filename,
      lineno: e.lineno,
      time: new Date().toISOString(),
    }
    const log = JSON.parse(localStorage.getItem('__crash_log') || '[]')
    log.push(entry)
    localStorage.setItem('__crash_log', JSON.stringify(log.slice(-10)))
    console.error('[crash-log] saved to localStorage.__crash_log', entry)
  })
  window.addEventListener('unhandledrejection', (e) => {
    const entry = { type: 'rejection', reason: String(e.reason), time: new Date().toISOString() }
    const log = JSON.parse(localStorage.getItem('__crash_log') || '[]')
    log.push(entry)
    localStorage.setItem('__crash_log', JSON.stringify(log.slice(-10)))
    console.error('[crash-log] saved to localStorage.__crash_log', entry)
  })
}

// Initialize AG Grid + AG Charts modules via dynamic imports so they
// don't bloat the entry chunk. These start loading immediately (in
// parallel with the app shell) and resolve before any grid/chart panel
// mounts — panels are behind lazy() + Suspense boundaries which are
// slower than a top-level dynamic import.
import('@/components/grid/ag-grid-setup').then(({ setupAgGrid }) => setupAgGrid())
import('ag-charts-enterprise').then(({ AllEnterpriseModule }) =>
  import('ag-charts-community').then(({ ModuleRegistry }) =>
    ModuleRegistry.registerModules([AllEnterpriseModule]),
  ),
)

// Affiliate tracking, before anything renders and outside AuthProvider: the referral
// cookie has to be set when an UNAUTHENTICATED visitor lands on /?via=<code>, which is
// long before they sign up. No-ops unless VITE_REWARDFUL_ENABLED is on for this stage.
loadRewardful()

// Configure Amplify Auth when Cognito pool ID is set (production).
// Must run before AuthProvider mounts (which calls restoreSession → getSession).
if (COGNITO.userPoolId) {
  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId: COGNITO.userPoolId,
        userPoolClientId: COGNITO.clientId,
      },
    },
  })
}

// Desktop app only: marks <html> while the window buttons cover the page, so
// each page's top row can move the window. A no-op on the web.
syncTitleBarAttribute()

/**
 * Provider hierarchy (outermost → innermost):
 * 1. Redux — global state
 * 2. Settings — theme mode, app preferences
 * 3. ChartInteraction — global wheel/drag/zoom-anchor feel, shared by every chart
 * 4. Auth — session restoration, user fetching
 * 5. Analytics — PostHog identify + tracking (no-op when key is empty)
 * 6. Router + PlanConstraints (inside AuthGuard per-route)
 */
// StrictMode removed: its double-mount behavior corrupts react-dnd's
// DragDropManager singleton inside react-mosaic-component, breaking
// panel drag on first interaction after page load.
createRoot(document.getElementById('root')!).render(
  <ReduxProvider store={store}>
    <SettingsProvider>
      <ChartInteractionProvider>
        <DeviceProvider>
          <AuthProvider>
            <AnalyticsProvider>
              <ChartColorPickerProvider>
                <ChartThemeColorsProvider>
                  <App />
                </ChartThemeColorsProvider>
              </ChartColorPickerProvider>
            </AnalyticsProvider>
          </AuthProvider>
        </DeviceProvider>
      </ChartInteractionProvider>
    </SettingsProvider>
  </ReduxProvider>,
)
